import { FC } from "react";
import { LayerGroup, Marker, Polygon } from "react-leaflet";
import { CountryEmissionsDTO, RangeValueDTO } from "../../../../../datasources/api-client/data-contracts";
import { calcPolygonCenter, getCountry, getHeatColor, labelIcon } from "../../utils";

interface Props {
	color: string;
	data: CountryEmissionsDTO[] | undefined;
	scale: RangeValueDTO[] | undefined;
}

/**
 * Returns a layer that shows a heatmap of different countries based on the given data, scale and color
 * I suggest setting the minZoom and maxZoom of the map to respectively 3 and 6, if this is the only layer of the map
 * MUST be used inside a Map or MapContainer component
 * @param heat array of heat data, each element contains the iso3 code of the country and the associated MeasurableValue
 * @param scale array of ranges ({min: number, max: number}) to calculate each country's scale level and color
 * @param color the color that will be used as a base to calculate each country's heat color. must be in hex format
 * @returns
 */
const HeatLayer: FC<Props> = ({ data, scale, color }) => {
	// create a (multi)polygon for each country data
	const polygons = data?.map((heatData) => {
		// get country data associated to the iso3 code
		const country = getCountry(heatData.iso3);
		if (!country) return null;

		// calculate the polygons color based on the heat value, the scale and the base color
		const heatColor = scale && getHeatColor(heatData.co2Wtw.value, scale, color);
		// create polygon options
		const polyOptions = {
			stroke: true,
			color: "#FFFFFF66",
			fillColor: heatColor,
			fillOpacity: 1,
			weight: 1,
		};

		// create the colored (multi)polygon
		return <Polygon key={`poly-${heatData.iso3}`} pathOptions={polyOptions} positions={country.geometry} />;
	});

	// create a label (Marker with a label as icon) for each country
	const markers = data?.map((heatData) => {
		// get country data associated to the iso3 code
		const country = getCountry(heatData.iso3);
		if (!country) return null;

		// create the label and place it on the map using a marker
		return (
			<Marker
				key={`label-${heatData.iso3}`}
				position={calcPolygonCenter(country.geometry[0])}
				icon={labelIcon(heatData.iso3, heatData.co2Wtw.value.toFixed(2).toString())}
			/>
		);
	});

	// returns a layergroup with both country polygons and labels
	return (
		<LayerGroup>
			{polygons}
			{markers}
		</LayerGroup>
	);
};

export default HeatLayer;
